import type { Breadcrumb, ErrorEvent } from "@sentry/nextjs";

const SESSION_COOKIE = "dealtracker_session";
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const SIGNED_TOKEN_PARAM = /([?&]token=)[^&\s#]+/gi;
const DOCUMENT_DOWNLOAD_PATH = /\/api\/documents\/file(\?[^#\s]*)?/gi;
const INVITE_TOKEN_PATH = /\/invite\/[A-Za-z0-9._-]+/gi;
const RESET_PASSWORD_TOKEN_PATH = /\/reset-password\/[A-Za-z0-9._-]+/gi;
const JWT_LIKE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const SENSITIVE_KEY_FRAGMENTS = [
  "password",
  "token",
  "secret",
  "authorization",
  "cookie",
  "session",
  "filename",
  "file",
  "content",
  "bytes",
  "download",
  "document",
  "body",
  "data",
];

export function getServerSentryDsn(): string | undefined {
  const dsn = process.env.SENTRY_DSN?.trim();
  return dsn || undefined;
}

export function getClientSentryDsn(): string | undefined {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN?.trim();
  return dsn || undefined;
}

/** Netlify deploy context tag for filtering preview noise from production. */
export function getNetlifyContextTag(): string {
  if (process.env.NETLIFY === "true" || process.env.NETLIFY === "1") {
    return process.env.CONTEXT?.trim() || "unknown";
  }
  return "local";
}

/** Sentry environment — mirrors Netlify CONTEXT when present. */
export function getSentryEnvironment(): string {
  const context = process.env.CONTEXT?.trim();
  if (context) return context;
  if (process.env.NODE_ENV === "production") return "production";
  return "development";
}

/** Deploy commit SHA for release/source-map correlation (Netlify build + runtime). */
export function getSentryRelease(): string | undefined {
  const sha =
    process.env.COMMIT_REF?.trim() ||
    process.env.NETLIFY_COMMIT_REF?.trim() ||
    process.env.VERCEL_GIT_COMMIT_SHA?.trim();
  return sha || undefined;
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment));
}

function scrubString(value: string): string {
  return value
    .replace(EMAIL_REGEX, "[email]")
    .replace(SIGNED_TOKEN_PARAM, "$1[Filtered]")
    .replace(DOCUMENT_DOWNLOAD_PATH, "/api/documents/file?[Filtered]")
    .replace(INVITE_TOKEN_PATH, "/invite/[Filtered]")
    .replace(RESET_PASSWORD_TOKEN_PATH, "/reset-password/[Filtered]")
    .replace(JWT_LIKE, "[Filtered]");
}

function scrubValue(value: unknown): unknown {
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map(scrubValue);
  if (value && typeof value === "object") {
    return scrubObject(value as Record<string, unknown>);
  }
  return value;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    out[key] = isSensitiveKey(key) ? "[Filtered]" : scrubValue(val);
  }
  return out;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const next = { ...breadcrumb };
  if (next.data) {
    next.data = scrubObject(next.data as Record<string, unknown>);
  }
  if (next.message) {
    next.message = scrubString(next.message);
  }
  return next;
}

function applyNetlifyTags(event: ErrorEvent): ErrorEvent {
  event.tags = {
    ...event.tags,
    netlify_context: getNetlifyContextTag(),
  };
  if (process.env.NETLIFY === "true" || process.env.NETLIFY === "1") {
    event.tags.netlify = "true";
  }
  return event;
}

function scrubRequest(event: ErrorEvent): void {
  if (event.request?.url) {
    event.request.url = scrubString(event.request.url);
  }

  if (event.request?.query_string && typeof event.request.query_string === "string") {
    event.request.query_string = scrubString(event.request.query_string);
  }

  if (event.request?.cookies) {
    for (const key of Object.keys(event.request.cookies)) {
      event.request.cookies[key] = "[Filtered]";
    }
  }

  if (event.request?.headers) {
    for (const key of Object.keys(event.request.headers)) {
      if (isSensitiveKey(key)) {
        event.request.headers[key] = "[Filtered]";
      }
    }
  }

  if (event.request?.data) {
    event.request.data = scrubObject(event.request.data as Record<string, unknown>);
  }
}

export function scrubEvent(event: ErrorEvent): ErrorEvent | null {
  applyNetlifyTags(event);
  scrubRequest(event);

  if (event.user?.email) {
    event.user.email = "[email]";
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }

  return event;
}

export function scrubBreadcrumbHook(breadcrumb: Breadcrumb): Breadcrumb | null {
  return scrubBreadcrumb(breadcrumb);
}

export function getSharedSentryOptions(dsn: string) {
  const release = getSentryRelease();

  return {
    dsn,
    environment: getSentryEnvironment(),
    ...(release ? { release } : {}),
    sendDefaultPii: false,
    tracesSampleRate: process.env.NODE_ENV === "development" ? 1.0 : 0.1,
    initialScope: {
      tags: {
        netlify_context: getNetlifyContextTag(),
      },
    },
    dataCollection: {
      userInfo: false,
      cookies: { deny: [SESSION_COOKIE] },
      httpBodies: [],
      httpHeaders: {
        request: { deny: ["authorization", "cookie", "set-cookie"] },
        response: { deny: ["set-cookie"] },
      },
      databaseQueryData: false,
    },
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumbHook,
  };
}
